import {NotificationManager} from '../components/common/react-notifications';
import {
  CONNECTED,
  ONLINE,
  STATION_TYPES,
  UNPAID,
} from '../constants/portalsConstants';
import {TRACKING_ACTIONS} from './tracking';
import {centsToDollarString} from './Utils';

const {
  ACCESS, ACCESS_PAYMENT, PUBLIC_PAYMENT, OPEN,
} = STATION_TYPES;

export const MODAL_TYPES = {
  ALREADY_CHARGING: 'already-charging',
  LIMIT_MET: 'limit-met',
  MUST_LOGIN: 'must-login',
  NEEDS_CARD: 'needs-card',
  PAST_DUE: 'past-due',
  PAYMENT_FAILED: 'payment-failed',
  STATION_IN_USE: 'station-in-use',
  SUBSCRIBED_TO_NOTIFICATIONS: 'text-notifications.subscribe',
  INVALID_SUBSCRIBED_TO_NOTIFICATIONS: 'text-notifications.invalid-subscribe',
  UNSUBSCRIBED_TO_NOTIFICATIONS: 'text-notifications.unsubscribe',
  SMS_VERIFIED: 'text-notifications.sms-verified',
  SMS_NOT_VERIFIED: 'text-notifications.sms-not-verified',
  SMS_NOT_RECEIVING: 'text-notifications.sms-not-receiving',
};

export const modalObjects = {
  alreadyCharging: {
    tracking: TRACKING_ACTIONS.ON_NAVIGATE,
    type: MODAL_TYPES.ALREADY_CHARGING,
    value: null,
  },
  limitMet: {
    tracking: TRACKING_ACTIONS.ON_NAVIGATE,
    type: MODAL_TYPES.LIMIT_MET,
    value: null,
  },
  mustLogin: {
    tracking: TRACKING_ACTIONS.ON_NAVIGATE,
    type: MODAL_TYPES.MUST_LOGIN,
    value: null,
  },
  needsCard: {
    tracking: TRACKING_ACTIONS.ON_NAVIGATE,
    type: MODAL_TYPES.NEEDS_CARD,
    value: null,
  },
  pastDue: (pastDueObject = {}, history) => ({
    arg: {billId: pastDueObject.billId, history},
    tracking: TRACKING_ACTIONS.ON_CLICK_PAY_PAST_DUE,
    type: MODAL_TYPES.PAST_DUE,
    value: centsToDollarString(pastDueObject.pastDueAmount),
  }),
  paymentFailed: {
    tracking: TRACKING_ACTIONS.ON_NAVIGATE,
    type: MODAL_TYPES.PAYMENT_FAILED,
    value: null,
  },
  stationInUse: {
    tracking: TRACKING_ACTIONS.ON_NAVIGATE,
    type: MODAL_TYPES.STATION_IN_USE,
    value: null,
  },
  subscribedToNotifications: {
    tracking: TRACKING_ACTIONS.ON_CLICK,
    type: MODAL_TYPES.SUBSCRIBED_TO_NOTIFICATIONS,
    value: null,
  },
  invalidSubscription: value => ({
    tracking: TRACKING_ACTIONS.ON_CLICK,
    type: MODAL_TYPES.INVALID_SUBSCRIBED_TO_NOTIFICATIONS,
    value,
  }),
  unsubscribedToNotifications: value => ({
    tracking: TRACKING_ACTIONS.ON_CLICK,
    type: MODAL_TYPES.UNSUBSCRIBED_TO_NOTIFICATIONS,
    value,
  }),
  smsVerified: value => ({
    tracking: TRACKING_ACTIONS.ON_CLICK,
    type: MODAL_TYPES.SMS_VERIFIED,
    value,
  }),
  smsNotVerified: {
    tracking: TRACKING_ACTIONS.ON_CLICK,
    type: MODAL_TYPES.SMS_NOT_VERIFIED,
    value: null,
  },
  smsNotReceiving: {
    tracking: TRACKING_ACTIONS.ON_CLICK,
    type: MODAL_TYPES.SMS_NOT_RECEIVING,
    value: null,
  },
};

export const getStationType = (driverLoginReq, paymentReq) => {
  if (driverLoginReq && paymentReq) return ACCESS_PAYMENT;
  if (driverLoginReq) return ACCESS;
  if (!driverLoginReq && paymentReq) return PUBLIC_PAYMENT;
  return OPEN;
};

/**
 * @param {object} [error] - Potential error that occurred while trying to start charging
 * @returns {?object} Modal object
 */
export const getChargeErrorModal = error => {
  if (!error) return null;
  if (error.billId) return modalObjects.pastDue(error.pastDueAmount);
  if (error.payment) return modalObjects.paymentFailed;
  if (error.limitMet) return modalObjects.limitMet;
  if (error.stationInUse) return modalObjects.stationInUse;
  if (error.alreadyCharging) return modalObjects.alreadyCharging;
  // TODO: Currently using this to debug unexpected errors more easily
  // Probbably isn't the best from a UX perspective
  // Should probably update to an error page or generic error message
  // vs showing the exact error Portals threw
  NotificationManager.error(error.message, 'Unexepected Error', 5000);
  return null;
};

/* eslint-disable-next-line complexity */
export const getButtonValues = ({
  userChargingElsewhere,
  connected,
  cost,
  deviceSessionStarted,
  charging,
  online,
  pending,
  hasPastDue,
  needsPayment,
  paymentChargingDisabled,
  paymentError,
  stationType,
  userIsAssignedUser,
}) => {
  if (userChargingElsewhere) return ['dashboard.button.my-session', 'toMySession'];
  if (userIsAssignedUser === false // access station is charging
    || (userIsAssignedUser === null && connected && charging) // open station is charging
  ) return ['station.occupied.expanded', 'toStationSelect', 'danger'];
  if (
    !online
    // We don't care adbout the remaining options for open charging stations
    || stationType === STATION_TYPES.OPEN
    // If payment charging is disabled (i.e. no collection account), do not want to allow
    // charging or past due payment
    || paymentChargingDisabled
  ) return [null, null];
  if (hasPastDue) return ['dashboard.button.pay-unpaid', 'showPastDueModal', 'danger'];
  if (connected && pending) return ['dashboard.button.charge-requested', null];
  // disconnected but have a devConnectSession, often right after a disconnect
  if (!connected && deviceSessionStarted) return [null, null];
  // connected w/ no devConnectSession => probably just need to wait for next interval
  if (connected && !deviceSessionStarted) return [null, null];
  if (paymentError || (needsPayment && cost && !charging)) return ['modal.needs-card.yes-button', 'toProfile', 'danger'];
  if (connected && !charging) return ['dashboard.button.start-charging', 'beginChargingFlow'];
  return [null, null];
};

/**
 * @param {object} from - object from location state
 * @param {string} from.path - Route path
 * @param {string} [from.url] - Route url
 * @param {number} siteId - Site ID
 * @returns {object} Ducktyped route match object
 */
export const formatMatchObject = (from, siteId) => {
  /*
    we could technically just use the second path for all cases,
    but the first path updates UI faster if the data is there (via 'from' param from location state)
    e.g. if navigating from the station list page, there is no lag in seeing the proper breadcrumbs
    w/ the first path, whereas if we only have the stationId, e.g. from direct url navigation,
    we need to wait until the siteId comes from the db
    This can be avoided by keeping the siteId in the URL, but it has been talked about for a long
    time to make portals urls more succinct
  */
  const manualMatchObject = from
    // 'first path' mentioned above
    ? {path: from.path, url: from.url}
    // second
    : {path: '/app/sites/:siteId', url: ''};
  if (siteId && !manualMatchObject.url) manualMatchObject.url = `/app/sites/${siteId}`;
  return manualMatchObject;
};

/**
 * Note: device and session params may be undefined if errors occur during the fetch
 * @param {number} userId -User ID
 * @param {object} [device] - response from device data API call
 * @param {object} [session] - response from current session API call
 * @returns {object} Current station values for state
 */
export const formatStationInfo = (userId, device, session) => {
  const rtn = {
    chargeStartTime: null,
    chargeStarted: session.chargeStarted,
    connected: device.connection === CONNECTED,
    deviceSessionStarted: !!session.dcosId,
    online: device.status === ONLINE,
    connectionTime: session.sessionStartTime, // Unix seconds
    energy: session.sessionEnergy || null, // Wh
    sessionStartTime: session.sessionStartTime,
    userIsAssignedUser: null,
  };
  if (session.paymentInfo) {
    rtn.estimatedCost = session.paymentInfo.amountHostPaid + session.paymentInfo.processingFee;
  }

  // From portals db, set during startCharging request, before device actually starts charging
  // Pretty sure chargeStarted ends up true even if user tries to start a charge with a bad card
  // device.assignedUser is based on the setting of User.currChargingStation
  // device.chargeStatus also seems unreliable
  // Not sure if there is just a more reliable value I'm not aware of at the moment
  rtn.startChargeInitiated = !!(rtn.connected && session.chargeStarted && device.assignedUser);

  // If falsy here then no session in progress, or open station, which doesn't need these values,
  // or discrepancy between portals and kitu db. So leave default values
  if (rtn.startChargeInitiated) {
    rtn.chargeStartTime = session.chargeStartTime; // Unix seconds
    // True if user-station association exists AND that user is the user making the request.
    // So this will be false if an access station is charging but a different user made the request.
    // Null for open stations and not-charging access stations.
    rtn.userIsAssignedUser = session.userId === userId;
  }

  return rtn;
};

/**
 * @param {object} response - startCharging API response
 * @returns {?object} Object to send to reducer or null if no recognizable error
 */
export const checkForStartChargingError = response => {
  if (response.type === UNPAID) {
    return {
      billId: response.billId,
      pastDueAmount: response.pastDueAmount,
    };
  }
  if (response.statusCode) return {payment: true};
  if (response.connectionLimitMetError) return {limitMet: true};
  if (
    (response.message === 'This station is already charging')
    || (response.chargeRequest && response.message === 'The requested station is already in use.')
  ) return {stationInUse: true};
  if (response.chargeRequest && response.message === 'User is charging at another station') return {alreadyCharging: true};
  return null;
};
